import { useState } from 'react'
import type { Panel } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  ColumnSettingsButton,
  StatusBadge,
  btnPrimary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { PanelEditModal } from './PanelEditModal'
import { useTableColumns } from '../../hooks/use-table-columns'

export function PanelListPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<Panel>({
    path: '/panels',
    filters: { search: debouncedSearch || undefined },
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('panels')

  function openEditModal(panel: Panel) {
    setSelectedId(panel.id)
    setIsNew(false)
    setModalOpen(true)
  }

  function openNewModal() {
    setSelectedId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  const columns: readonly Column<Panel>[] = [
    { key: 'code', header: 'Code', sortable: true, filterable: true, render: (p) => <strong>{p.code}</strong> },
    { key: 'name', header: 'Name', sortable: true, filterable: true, render: (p) => p.name },
    { key: 'tests', header: 'Tests', render: (p) => `${p.testTypeIds.length}` },
    { key: 'reflex', header: 'Reflex', render: (p) => (p.isReflexive ? 'Yes' : '--') },
    {
      key: 'isSystem',
      header: 'System',
      render: (p) => (p.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
    },
    { key: 'status', header: 'Status', sortable: true, render: (p) => (p.isActive ? 'Active' : 'Archived') },
  ]

  return (
    <div>
      <PageHeader
        title="Panels"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={openNewModal} style={btnPrimary}>
              Add Panel
            </button>
          </>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openEditModal}
            emptyMessage="No panels found. Create one to bundle test types into reusable orderable groups."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <PanelEditModal
        panelId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => refresh()}
        onArchived={() => refresh()}
      />
    </div>
  )
}
